import { Cell, Row } from "@sa/rep-mdde";
import { DictionariesService, Dictionary, DictionaryItem } from "@sa/rep-mdm";
import { OLAPResult } from "@sa/rep-olap";
import { clone, equal } from "../../../../shared";

type Filter = (item : DictionaryItem) => boolean;
export class DataSourcesService {
  private readonly _dictionaryService: DictionariesService;
  private _promiseDictionary: Promise<Dictionary> | undefined;
  private _promiseDictionaryItems: Promise<DictionaryItem[]> | undefined;

  constructor(dictionaryService: DictionariesService) {
    this._dictionaryService = dictionaryService;
  }

  public clearDataSources(olap: OLAPResult) {
    const newOlap = clone(olap);
    const { data } = newOlap;
    const dataInColumns = this._dataInColumns(olap);
    if (dataInColumns) {
      const searchRows = this._getSearchRows(olap);
      data.rows = data.rows.filter(r => !searchRows.some(x => equal(x, r)));
    } else {
      for (const row of data.rows) {
        const searchCells = this._getSearchCells(row);
        row.cells = row.cells.filter(c => !searchCells.some(x => equal(x, c)));
      }
    }
    return newOlap;
  }

  public async getDataSources(slide: number, businessUnit: number, commodity: number, filter?: Filter, calendar?: string) {
    // Отправляем запрос на поучение справчников и продолжаем обрабатывать код
    const items$ = this._getDictionaryItems();
    const itemsDisplay$ = this._getDisplayDictionaryItems(slide, businessUnit, commodity, calendar);
    const sources: Set<string> = new Set();

    const result: string[] = [];

    const [items, itemsDisplayAll] = await Promise.all([items$, itemsDisplay$]);
    const itemsDisplay = filter ? itemsDisplayAll.filter(filter) : itemsDisplayAll;
    for (const itemDisplay of itemsDisplay) {
      const sourceIdentifier = itemDisplay.getValue<string>('source');
      if (sourceIdentifier && !sources.has(sourceIdentifier)) {
        sources.add(sourceIdentifier);
        const sourceData = items.find((item: DictionaryItem) => sourceIdentifier === item.getValue<string>('identifier'));
        if (sourceData) {
          const name = sourceData.getValue<string>('name');
          const lastUpdateDateAttrVal = sourceData.getValue<string>('lastUpdate');
          const lastUpdateDate = lastUpdateDateAttrVal ? new Date(lastUpdateDateAttrVal) : null;
          const lastUpdateDateString = lastUpdateDate?.toDateString();
          const month = lastUpdateDateString?.split(' ')[1];
          const year = lastUpdateDateString?.split(' ')[3];
          const candidateResultRecord = [name, month, year].filter(Boolean).join(' ');
          if (candidateResultRecord) {
            result.push(candidateResultRecord);
          }
        }
      }
    }
    return result.join(', ');
  }
  
  /** 
  * @deprecated 
  * Use the new:
  *```ts 
  * async getDataSources(slide: number, businessUnit: number, commodity: number, filter?: Filter)
  *```
  */
  public async getDataSource(olap: OLAPResult) {
    // Отправляем запрос на поучение справчников и продолжаем обрабатывать код
    const items$ = this._getDictionaryItems();
    const sources: Set<string> = new Set();
    const dataInColumns = this._dataInColumns(olap);
    const { data } = olap;
    const searchRows = dataInColumns ? this._getSearchRows(olap) : data.rows;
    for (const row of searchRows) {
      const cells = !dataInColumns ? this._getSearchCells(row) : row.cells;
      for (const cell of cells) {
        const source = (cell.value as any as string)?.trim();
        if (source) {
          sources.add(source);
        }
      }
    }

    const result: string[] = [];

    // Тут начинаем ждать полученные items
    const items = await items$;
    for (const sourceId of [...sources.values()]) {
      const item = items.find(i => i.getValue("identifier") === sourceId);
      if (item) {
        const name = item.getValue<string>('name');
        const lastUpdate = new Date(item.getValue<string>('lastUpdate')!);
        const strLastUpdate = lastUpdate?.toDateString();
        const strMonth = strLastUpdate?.split(' ')[1];
        const strYear = strLastUpdate?.split(' ')[3];
        result.push([name, strMonth, strYear].filter(Boolean).join(' '));
      }
    }
    return result.join(', ');
  }

  private async _getDisplayDictionaryItems(slide: number, businessUnit: number, commodity: number, calendar?: string): Promise<DictionaryItem[]> {
    const dictionary = await this._dictionaryService.getDictionary({
      id: 'slides-sources-display', parameters: {
        slide,
        business_unit: businessUnit,
        olap_commodity: commodity,
        calendar
      }
    });

    const dictionaryItems = await dictionary.getAll();
    return dictionaryItems;
  }

  private async _getDictionaryItems(): Promise<DictionaryItem[]> {
    if (!this._promiseDictionary) {
      this._promiseDictionary = this._dictionaryService.getDictionary({ id: 'slides-sources', parameters: {} });
    }
    const dictionary = await this._promiseDictionary;

    if (!this._promiseDictionaryItems) {
      this._promiseDictionaryItems = dictionary.getAll();
    }
    const dictionaryItems = await this._promiseDictionaryItems;

    return dictionaryItems;
  }

  private _dataInColumns(olap: OLAPResult): boolean {
    const { rows } = olap.data;
    const searchRows = rows.find(r => r.selection['Measures'] === "search");
    return Boolean(searchRows);
  }

  private _getSearchRows(olap: OLAPResult): Row[] {
    const { rows } = olap.data;
    return rows.filter(r => r.selection['Measures'] === "search");
  }

  private _getSearchCells(row: Row): Cell[] {
    const { cells } = row;
    return cells.filter(c => c.selection['Measures'] === "search");
  }
}